import type { ComponentType, ReactNode } from 'react';
import { Button } from '@reflex/ui/components/ui/button';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { ResourceChip } from '@/components/resource-preview/ResourceChip';
import { AgentTypeChip } from '@reflex/ui/components/agent/AgentTypeChip';
import { ModelChip } from '@reflex/ui/components/agent/ModelChip';
import { renderPersonaIcon } from '@reflex/plugin-agent-persona/web/PersonaIconPicker';
import { Globe, GitBranch, FileText } from 'lucide-react';
import { getPersonaRepo, type AgentPersona } from '@reflex/plugin-agent-persona/shared/types';
import { personaInitials } from './persona-utils';

export interface PersonaDetailCardProps {
  persona: AgentPersona;
  onClear: () => void;
}

interface MetaChipProps {
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
  title?: string;
}

function MetaChip({ icon: Icon, children, title }: MetaChipProps) {
  return (
    <span
      title={title}
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{children}</span>
    </span>
  );
}

export function PersonaDetailCard({ persona, onClear }: PersonaDetailCardProps) {
  const repo = getPersonaRepo(persona);
  const repoLabel = repo?.repoSlug.replace('https://github.com/', '');
  const repoBranch = repo?.repoBranch;

  return (
    <div
      className="space-y-3 rounded-lg border border-border bg-muted/20 p-3"
      data-testid="persona-detail-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted/60 ring-1 ring-inset ring-border/60">
          {(() => {
            const rendered = renderPersonaIcon(persona.icon, 'h-4 w-4 text-muted-foreground');
            if (rendered) return rendered;
            return (
              <span className="text-xs font-semibold tracking-tight text-muted-foreground">
                {personaInitials(persona.name)}
              </span>
            );
          })()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{persona.name}</p>
          {persona.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {persona.description}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <AgentTypeChip agentType={persona.agentType} />
        {persona.model && <ModelChip model={persona.model} />}
        {repoLabel && (
          <MetaChip icon={Globe} title={repoBranch ? `${repoLabel} (${repoBranch})` : repoLabel}>
            {repoLabel}
            {repoBranch && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-muted-foreground/60">
                <GitBranch className="h-2.5 w-2.5" />
                {repoBranch}
              </span>
            )}
          </MetaChip>
        )}
        {persona.blueprintName && (
          <ResourceChip kind="blueprints" id={persona.blueprintName} name={persona.blueprintName} />
        )}
      </div>

      {persona.systemPrompt && (
        <div className="space-y-1">
          <SectionLabel as="span" className="text-3xs">
            System prompt
          </SectionLabel>
          <p className="max-h-24 overflow-y-auto rounded bg-muted/30 px-2 py-1.5 font-mono text-[11px] text-muted-foreground">
            {persona.systemPrompt}
          </p>
        </div>
      )}

      {persona.defaultPrompt && (
        <div className="space-y-1">
          <SectionLabel as="span" className="text-3xs">
            Default prompt
          </SectionLabel>
          <div className="flex items-start gap-2 rounded-md border border-dashed border-border/60 bg-muted/20 px-2.5 py-1.5">
            <FileText className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/70" />
            <p className="line-clamp-2 text-[11px] italic text-muted-foreground">
              {persona.defaultPrompt}
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClear}>
          Clear persona
        </Button>
      </div>
    </div>
  );
}
